import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/auth-context';
import axios from 'axios';
import { toast } from 'react-toastify';
import { rootUrl, formatToZMW } from '../../helpers';
import { 
  FaUsers, 
  FaMoneyBillWave, 
  FaReceipt, 
  FaFileInvoiceDollar, 
  FaProjectDiagram, 
  FaBook, 
  FaChartLine,
  FaSpinner,
  FaUserCheck,
  FaUserClock,
  FaCalendarAlt
} from 'react-icons/fa';
import { MdDashboard } from 'react-icons/md';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = useAuth();

  const fetchStats = async () => {
    try {
      const response = await axios.get(rootUrl(`/getAdminStats/${auth.user.cid}`));
      if (response.data.status === 200) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.user?.cid) {
      fetchStats();
    }
  }, [auth.user?.cid]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-2xl border border-gray-200">
        <FaSpinner className="animate-spin text-blue-950 text-5xl mb-4" />
        <p className="text-gray-600 font-medium">Loading dashboard statistics...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-200">
        <MdDashboard className="text-gray-400 text-5xl mx-auto mb-4" />
        <p className="text-gray-600 text-lg font-medium">No statistics available</p>
        <p className="text-gray-500 text-sm mt-2">Please check back later</p>
      </div>
    );
  }

  const StatCard = ({ title, value, icon, color, subtitle, gradient }) => {
    const colorMap = {
      blue: { bg: 'from-blue-500 to-blue-600', iconBg: 'bg-blue-100', iconColor: 'text-blue-600', border: 'border-blue-500' },
      green: { bg: 'from-green-500 to-green-600', iconBg: 'bg-green-100', iconColor: 'text-green-600', border: 'border-green-500' },
      red: { bg: 'from-red-500 to-red-600', iconBg: 'bg-red-100', iconColor: 'text-red-600', border: 'border-red-500' },
      purple: { bg: 'from-purple-500 to-purple-600', iconBg: 'bg-purple-100', iconColor: 'text-purple-600', border: 'border-purple-500' },
      yellow: { bg: 'from-yellow-500 to-yellow-600', iconBg: 'bg-yellow-100', iconColor: 'text-yellow-600', border: 'border-yellow-500' },
      orange: { bg: 'from-orange-500 to-orange-600', iconBg: 'bg-orange-100', iconColor: 'text-orange-600', border: 'border-orange-500' },
    };
    
    const colors = colorMap[color] || colorMap.blue;
    
    return (
      <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 overflow-hidden group">
        <div className={`h-1 bg-gradient-to-r ${colors.bg}`}></div>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
              <p className="text-3xl font-bold text-gray-900">{value}</p>
              {subtitle && (
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  {subtitle}
                </p>
              )}
            </div>
            <div className={`${colors.iconBg} rounded-xl p-3 group-hover:scale-110 transition-transform duration-200`}>
              <div className={colors.iconColor}>
                {icon}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 rounded-xl p-3">
            <MdDashboard className="text-2xl" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-blue-100 text-sm mt-1">Overview of system statistics and metrics</p>
          </div>
        </div>
      </div>

      {/* Key Financial Metrics - Top Priority */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-2">
            <FaMoneyBillWave className="text-white text-xl" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">Financial Overview</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Income"
            value={formatToZMW(stats.transactions.income)}
            icon={<FaMoneyBillWave className="text-xl" />}
            color="green"
          />
          <StatCard
            title="Total Expenses"
            value={formatToZMW(stats.transactions.expenses)}
            icon={<FaMoneyBillWave className="text-xl" />}
            color="red"
          />
          <StatCard
            title="Net Balance"
            value={formatToZMW(stats.transactions.net)}
            icon={<FaChartLine className="text-xl" />}
            color={stats.transactions.net >= 0 ? 'green' : 'red'}
            subtitle={stats.transactions.net >= 0 ? 'Positive balance' : 'Negative balance'}
          />
          <StatCard
            title="Total Transactions"
            value={stats.transactions.total}
            icon={<FaBook className="text-xl" />}
            color="purple"
          />
        </div>
      </div>

      {/* User Management & System Overview - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Statistics */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 rounded-lg p-2">
              <FaUsers className="text-blue-600 text-xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">User Management</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              title="Total Users"
              value={stats.users.total}
              icon={<FaUsers className="text-xl" />}
              color="blue"
            />
            <StatCard
              title="Active Users"
              value={stats.users.active}
              icon={<FaUserCheck className="text-xl" />}
              color="green"
              subtitle={`${((stats.users.active / stats.users.total) * 100).toFixed(1)}%`}
            />
            <StatCard
              title="Pending Users"
              value={stats.users.pending}
              icon={<FaUserClock className="text-xl" />}
              color="yellow"
              subtitle="Awaiting approval"
            />
          </div>
        </div>

        {/* System Overview */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-purple-100 rounded-lg p-2">
              <FaProjectDiagram className="text-purple-600 text-xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">System Overview</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              title="Banks"
              value={stats.banks.total}
              icon={<FaUsers className="text-xl" />}
              color="blue"
            />
            <StatCard
              title="Budget Items"
              value={stats.budget.items}
              icon={<FaMoneyBillWave className="text-xl" />}
              color="green"
              subtitle={`${formatToZMW(stats.budget.amount)}`}
            />
            <StatCard
              title="Projects"
              value={stats.projects.total}
              icon={<FaProjectDiagram className="text-xl" />}
              color="purple"
            />
          </div>
        </div>
      </div>

      {/* Business Entities */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-orange-100 rounded-lg p-2">
            <FaReceipt className="text-orange-600 text-xl" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">Business Entities</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Suppliers"
            value={stats.suppliers.total}
            icon={<FaUsers className="text-xl" />}
            color="orange"
          />
          <StatCard
            title="Customers"
            value={stats.customers.total}
            icon={<FaUsers className="text-xl" />}
            color="blue"
          />
          <StatCard
            title="Receipts"
            value={stats.receipts.total}
            icon={<FaReceipt className="text-xl" />}
            color="green"
            subtitle={`Total: ${formatToZMW(stats.receipts.amount)}`}
          />
          <StatCard
            title="Bills"
            value={stats.bills.total}
            icon={<FaFileInvoiceDollar className="text-xl" />}
            color="red"
            subtitle={`Total: ${formatToZMW(stats.bills.amount)}`}
          />
        </div>
      </div>

      {/* Activity & Records */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <StatCard
          title="Recent Transactions"
          value={stats.recent.transactions}
          icon={<FaCalendarAlt className="text-xl" />}
          color="blue"
          subtitle="Last 7 days"
        />
        <StatCard
          title="Recent Receipts"
          value={stats.recent.receipts}
          icon={<FaReceipt className="text-xl" />}
          color="green"
          subtitle="Last 7 days"
        />
        <StatCard
          title="Journal Entries"
          value={stats.journals.total}
          icon={<FaBook className="text-xl" />}
          color="purple"
        />
      </div>
    </div>
  );
}
